import { useMemo } from 'react'
import { formatCurrencyCompact, type ParetoSkuBar } from './paretoCurveMockData'

interface Props {
  bars: ParetoSkuBar[]
}

const WIDTH = 900
const HEIGHT = 320
const PAD_LEFT = 56
const PAD_RIGHT = 40
const PAD_TOP = 24
const PAD_BOTTOM = 76

const BAR_COLOR = '#1F77FF'
const LINE_COLOR = '#0B2C72'
const AXIS_COLOR = '#9aa3ad'
const GRID_COLOR = '#e7eaef'
const LABEL_COLOR = '#1c1c1c'

export function ParetoChart({ bars }: Props) {
  const { plotW, plotH, maxSales, yTicks, barWidth, gap, points } =
    useMemo(() => {
      const plotW = WIDTH - PAD_LEFT - PAD_RIGHT
      const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM
      const maxSales = Math.max(...bars.map(b => b.salesDollar), 1)
      const niceMax = niceCeiling(maxSales)
      const tickCount = 5
      const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => {
        const v = (niceMax * i) / tickCount
        return { value: v, y: plotH - (v / niceMax) * plotH }
      })
      const slot = plotW / bars.length
      const barWidth = slot * 0.78
      const gap = slot - barWidth
      const points = bars
        .map((b, i) => {
          const x = i * slot + slot / 2
          const y = plotH - b.cumulativePct * plotH
          return `${x.toFixed(2)},${y.toFixed(2)}`
        })
        .join(' ')
      return { plotW, plotH, maxSales: niceMax, yTicks, barWidth, gap, points }
    }, [bars])

  const slot = plotW / bars.length

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-foreground">
          Sales $ and Cumulative % of Total by sku
        </div>
      </div>
      <div className="mb-2 flex items-center gap-4 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: BAR_COLOR }}
          />
          Sales $
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: LINE_COLOR }}
          />
          Cumulative % of Total
        </span>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        preserveAspectRatio="none"
      >
        <g transform={`translate(${PAD_LEFT}, ${PAD_TOP})`}>
          {/* Y-axis gridlines + left ticks (Sales $) + right ticks (%) */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line
                x1={0}
                x2={plotW}
                y1={t.y}
                y2={t.y}
                stroke={GRID_COLOR}
                strokeWidth={1}
              />
              <text
                x={-8}
                y={t.y + 3}
                textAnchor="end"
                fontSize={10}
                fill={LABEL_COLOR}
              >
                {formatCurrencyCompact(t.value)}
              </text>
              <text
                x={plotW + 8}
                y={t.y + 3}
                textAnchor="start"
                fontSize={10}
                fill={LABEL_COLOR}
              >
                {Math.round((t.value / maxSales) * 100)}%
              </text>
            </g>
          ))}

          {/* Bars (with hover tooltip showing SKU + sales $) */}
          {bars.map((b, i) => {
            const h = (b.salesDollar / maxSales) * plotH
            const x = i * slot + gap / 2
            const y = plotH - h
            return (
              <rect
                key={i}
                x={x}
                y={y}
                width={barWidth}
                height={h}
                fill={BAR_COLOR}
              >
                <title>{`SKU ${b.sku} — ${formatCurrencyCompact(b.salesDollar)}`}</title>
              </rect>
            )
          })}

          {/* Cumulative % line */}
          <polyline
            points={points}
            fill="none"
            stroke={LINE_COLOR}
            strokeWidth={1.6}
          />
          {/* Point markers + labels every 3rd point to avoid crowding */}
          {bars.map((b, i) => {
            const x = i * slot + slot / 2
            const y = plotH - b.cumulativePct * plotH
            const showLabel = i % 3 === 1 || i === bars.length - 1
            return (
              <g key={`pt-${i}`}>
                <circle cx={x} cy={y} r={1.8} fill={LINE_COLOR} />
                {showLabel && (
                  <text
                    x={x}
                    y={y - 6}
                    textAnchor="middle"
                    fontSize={9}
                    fill={LABEL_COLOR}
                  >
                    {Math.round(b.cumulativePct * 100)}%
                  </text>
                )}
              </g>
            )
          })}

          {/* X-axis baseline */}
          <line
            x1={0}
            x2={plotW}
            y1={plotH}
            y2={plotH}
            stroke={AXIS_COLOR}
            strokeWidth={1}
          />

          {/* X-axis bar labels (SKU codes) — vertical so every bar gets one */}
          {bars.map((b, i) => {
            const x = i * slot + slot / 2
            return (
              <text
                key={`xl-${i}`}
                x={x}
                y={plotH + 8}
                textAnchor="end"
                fontSize={9}
                fill={LABEL_COLOR}
                transform={`rotate(-90 ${x} ${plotH + 8})`}
              >
                {b.sku}
              </text>
            )
          })}

          {/* Axis titles */}
          <text
            transform={`translate(${-44}, ${plotH / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize={11}
            fill={LABEL_COLOR}
          >
            Sales $
          </text>
          <text
            x={plotW / 2}
            y={plotH + 68}
            textAnchor="middle"
            fontSize={11}
            fill={LABEL_COLOR}
          >
            sku
          </text>
        </g>
      </svg>
    </div>
  )
}

function niceCeiling(value: number): number {
  if (value <= 0) return 1
  const exponent = Math.floor(Math.log10(value))
  const fraction = value / Math.pow(10, exponent)
  let niceFraction: number
  if (fraction <= 1) niceFraction = 1
  else if (fraction <= 2) niceFraction = 2
  else if (fraction <= 2.5) niceFraction = 2.5
  else if (fraction <= 5) niceFraction = 5
  else niceFraction = 10
  return niceFraction * Math.pow(10, exponent)
}
